// A page that shows FeatureReleaseUpdates changes whenever a release note links to it, so
// its lastmod (sitemaps, the Article dateModified in app.vue) is the later of its
// data/seo-routes.ts date and the newest release card it shows. Plain JS: the build
// scripts (scripts/localization-utils.mjs), the dev sitemap route and modules/page-data.ts
// all import it, so every place a lastmod is emitted agrees.

// The routes whose templates render <FeatureReleaseUpdates> for their own path:
// FeatureDetailPage and the custom feature pages, pages/integrations/[slug].vue, TutorialPage
// and SeoLandingPage (solutions, plus the two /features pages built on it).
// scripts/release-lastmod.test.mjs checks this against the page files.
export const releaseUpdatesPath = /^\/(?:features|integrations|tutorials|solutions)\/[^/]+$/;

// FeatureReleaseUpdates' default card count; no page passes another limit.
export const releaseUpdatesLimit = 3;

// Same matching as FeatureReleaseUpdates and releasesLinkingTo: trailing slashes ignored.
export const releaseLinkPath = (path) => path.replace(/\/+$/, '') || '/';

/** path -> the releases whose features link there, newest first (the order data/release-notes.ts sorts to). */
export function releasesByLinkedPath(releases) {
  const byPath = {};
  for (const release of [...releases].sort((a, b) => b.date.localeCompare(a.date))) {
    const paths = new Set(release.features.flatMap((feature) => feature.links.map((link) => releaseLinkPath(link.href))));
    for (const path of paths) (byPath[path] ??= []).push(release);
  }
  return byPath;
}

const utcToday = () => new Date().toISOString().slice(0, 10);

/**
 * path -> date of the newest release card that path's page shows. A release dated after
 * `today` never counts, so a lastmod never moves past today.
 */
export function releaseCardDates(releases, today = utcToday()) {
  const dates = {};
  for (const [path, linked] of Object.entries(releasesByLinkedPath(releases))) {
    if (!releaseUpdatesPath.test(path)) continue;
    const newest = linked.slice(0, releaseUpdatesLimit).map((release) => release.date).filter((date) => date <= today).sort().at(-1);
    if (newest) dates[path] = newest;
  }
  return dates;
}

/** The lastmod to emit for a route: its seo-routes date, or a newer release card's date. */
export const effectiveLastmod = (path, lastmod, cardDates) => {
  const card = cardDates[releaseLinkPath(path)];
  return card && card > lastmod ? card : lastmod;
};
